<template>
  <div class="my-travel">
    <section
      v-if="!isInboundRebookingView"
      class="my-travel__outbound"
    >
      <h2 class="my-travel__label">
        {{ $t('travelPlanner.myTravel.outbound') }}
      </h2>
      <p class="my-travel__od">{{ origin }} - {{ destination }}</p>
      <div v-if="outboundTravel">
        <MyTravelList
          v-if="outboundSelectedTickets.tickets.length"
          :travelDate="formattedDepartureDate"
          :travelData="outboundTravelData"
          direction="outbound"
          :passengerCounts="outboundPassengerCounts"
          :groupedEquipment="outboundGroupedEquipment"
          :selectedSeats="outboundSelectedSeats"
          :totalSeatPrice="normalizePrice(outboundTotalSeatPrice, 'NOK', false)"
          :totalEquipmentPrice="normalizePrice(outboundTotalEquipmentPrice)"
          :selectedTickets="outboundSelectedTickets.tickets"
          :discounts="discounts"
          :totalPriceToBePaid="
            overrideTotalPrice ||
            (isRebookingView ? rebookingPrice : totalPriceToBePaid)
          "
          :showTotal="showTotalPrice && !hasReturnTravel"
          :showDiscounts="showTotalPrice && !hasReturnTravel"
        />
      </div>
      <span
        v-if="!outboundTravel"
        class="my-travel__label"
      >
        {{ $t('travelPlanner.myTravel.notSelected.outbound') }}
      </span>
    </section>
    <section
      v-if="hasReturnTravel || isInboundRebookingView"
      class="my-travel__inbound"
    >
      <h2 class="my-travel__label">
        {{ $t('travelPlanner.myTravel.inbound') }}
      </h2>
      <p class="my-travel__od">
        <template
          v-if="
            isInboundRebookingView &&
            inboundTravel &&
            inboundTravel.routes &&
            inboundTravel.routes[0] &&
            inboundTravel.routes[0].legs &&
            inboundTravel.routes[0].legs[0]
          "
        >
          {{ inboundTravel.routes[0].legs[0].departure_station.name }} -
          {{ inboundTravel.routes[0].legs[0].arrival_station.name }}
        </template>
        <template v-else>{{ destination }} - {{ origin }}</template>
      </p>
      <div v-if="inboundTravel">
        <MyTravelList
          v-if="inboundSelectedTickets.tickets.length"
          :travelDate="formattedReturnDate"
          :travelData="inboundTravelData"
          direction="inbound"
          :passengerCounts="inboundPassengerCounts"
          :groupedEquipment="inboundGroupedEquipment"
          :selectedSeats="inboundSelectedSeats"
          :totalSeatPrice="normalizePrice(inboundTotalSeatPrice, 'NOK', false)"
          :totalEquipmentPrice="normalizePrice(inboundTotalEquipmentPrice)"
          :selectedTickets="inboundSelectedTickets.tickets"
          :discounts="discounts"
          :totalPriceToBePaid="
            overrideTotalPrice ||
            (isRebookingView ? rebookingPrice : totalPriceToBePaid)
          "
          :showTotal="showTotalPrice"
          :showDiscounts="showTotalPrice"
        />
      </div>
      <span
        v-if="!inboundTravel"
        class="my-travel__label"
      >
        {{ $t('travelPlanner.myTravel.notSelected.inbound') }}
      </span>
    </section>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed } from 'vue'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { normalizePrice } from '@/utils/priceNormalization'
  import { formatShortNorwegianDate } from '@/utils/dateFormatter'
  import MyTravelList from './MyTravelList.vue'

  export default defineComponent({
    name: 'MyTravel',
    components: {
      MyTravelList,
    },
    props: {
      isRebooking: {
        type: Boolean,
        default: false,
      },
      isInboundRebooking: {
        type: Boolean,
        default: false,
      },
      showTotalPrice: {
        type: Boolean,
        default: false,
      },
      overrideTotalPrice: {
        type: Object,
        default: undefined,
      },
    },
    setup(props) {
      const myTravelStore = useMyTravelStore()
      const journeyStore = useJourneyStore()

      const isInboundRebookingView = computed(() => {
        return (
          props.isInboundRebooking || myTravelStore.rebookingType === 'inbound'
        )
      })

      const isRebookingView = computed(() => {
        return (
          props.isRebooking ||
          myTravelStore.isRebooking ||
          myTravelStore.rebookingType !== null
        )
      })

      const rebookingPrice = computed(() => {
        if (
          myTravelStore.rebookingType === 'inbound' &&
          myTravelStore.rebookingDisplayTotal?.amount !== undefined
        ) {
          return myTravelStore.rebookingDisplayTotal
        }

        const totalFee = myTravelStore.totalRebookingFee
        if (totalFee) {
          return totalFee
        }
        return myTravelStore.rebookingDisplayTotal
      })
      return {
        isInboundRebookingView,
        isRebookingView,
        rebookingPrice,
        rebookingDisplayTotal: computed(
          () => myTravelStore.rebookingDisplayTotal
        ),
        origin: computed(() => myTravelStore.origin),
        destination: computed(() => myTravelStore.destination),
        outboundTravel: computed(() => myTravelStore.outboundTravel),
        inboundTravel: computed(() => myTravelStore.inboundTravel),
        outboundTravelData: computed(() => myTravelStore.outboundTravelData),
        inboundTravelData: computed(() => myTravelStore.inboundTravelData),
        hasReturnTravel: computed(() => {
          const hasReturn = myTravelStore.hasReturnTravel
          return hasReturn
        }),
        outboundPassengerCounts: computed(
          () => myTravelStore.outboundPassengerCounts
        ),
        inboundPassengerCounts: computed(
          () => myTravelStore.inboundPassengerCounts
        ),
        outboundGroupedEquipment: computed(() => {
          const equipment = myTravelStore.outboundGroupedEquipment
          return equipment
        }),
        inboundGroupedEquipment: computed(() => {
          const equipment = myTravelStore.inboundGroupedEquipment
          return equipment
        }),
        outboundSelectedSeats: computed(
          () => myTravelStore.outboundSelectedSeats
        ),
        inboundSelectedSeats: computed(
          () => myTravelStore.inboundSelectedSeats
        ),
        outboundTotalSeatPrice: computed(
          () => myTravelStore.outboundTotalSeatPrice
        ),
        inboundTotalSeatPrice: computed(
          () => myTravelStore.inboundTotalSeatPrice
        ),
        outboundTotalEquipmentPrice: computed(
          () => myTravelStore.outboundTotalEquipmentPrice
        ),
        inboundTotalEquipmentPrice: computed(
          () => myTravelStore.inboundTotalEquipmentPrice
        ),
        outboundSelectedTickets: computed(
          () => myTravelStore.outboundSelectedTickets
        ),
        inboundSelectedTickets: computed(
          () => myTravelStore.inboundSelectedTickets
        ),
        discounts: computed(() => myTravelStore.discounts),
        totalPriceToBePaid: computed(() => myTravelStore.totalPriceToBePaid),
        formattedDepartureDate: computed(() => {
          console.log('[MYTRAVEL] Computing formattedDepartureDate:', {
            journeyStoreDepartureDate: journeyStore.departureDate,
            myTravelStoreOutboundDate: myTravelStore.outboundDate
          })
          
          // Prioritize journeyStore date for rebooking consistency
          if (journeyStore.departureDate) {
            const result = formatShortNorwegianDate(journeyStore.departureDate)
            console.log('[MYTRAVEL] Using journeyStore departure date:', result)
            return result
          }
          // Fall back to myTravelStore date
          console.log('[MYTRAVEL] Using myTravelStore outbound date:', myTravelStore.outboundDate)
          return myTravelStore.outboundDate
        }),
        formattedReturnDate: computed(() => {
          console.log('[MYTRAVEL] Computing formattedReturnDate:', {
            journeyStoreReturnDate: journeyStore.returnDate,
            myTravelStoreReturnDate: myTravelStore.returnDate
          })
          
          // Prioritize journeyStore date for rebooking consistency
          if (journeyStore.returnDate) {
            const result = formatShortNorwegianDate(journeyStore.returnDate)
            console.log('[MYTRAVEL] Using journeyStore return date:', result)
            return result
          }
          
          // Fall back to myTravelStore date
          let date = myTravelStore.returnDate

          if (!date) {
            const savedReturnDate = sessionStorage.getItem(
              'rebooking_return_date'
            )
            if (savedReturnDate) {
              date = formatShortNorwegianDate(savedReturnDate)
              console.log('[MYTRAVEL] Using saved return date from session:', date)
            }
          }

          console.log('[MYTRAVEL] Final return date result:', date)
          return date
        }),
        normalizePrice,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .my-travel {
    padding: 1.875rem;
    text-align: left;
    border-radius: $border-radius;
    box-shadow: $box-shadow-card;
    background-color: $clr-white;
    color: $clr-primary-800;

    .drawer-overlay & {
      padding: 0;
      box-shadow: none;
      border-radius: 0;
    }

    &__label {
      @include body-medium;
      margin-bottom: 0;
    }

    &__od {
      @include title-medium;
      margin-top: 0;
      margin-bottom: rem(24);
      color: inherit;
    }

    &__date {
      @include title-medium;
    }

    &__inbound {
      border-top: 1px solid $clr-primary-100;
    }
  }
</style>
